import type { api } from '@stageplottter/core';

export type NotificationType = api.Notification['type'];

export interface NotificationView {
  id: string;
  type: NotificationType;
  read: boolean;
  createdAt: string;
  boardId: string | null;
  boardName: string;
  commentId: string | null;
  linkId: string | null;
  linkName: string;
  authorName: string;
  excerpt: string;
  guestName: string;
  message: string;
  expiresAt: string | null;
  openItemId: string | null;
  title: string;
  actorName: string;
  status: string | null;
}

const str = (p: Record<string, unknown>, k: string): string | null => {
  const v = p[k];
  return typeof v === 'string' && v !== '' ? v : null;
};

/** Reads the loosely typed payload defensively (server payloads per type, see services/notifications). */
export function toView(n: api.Notification): NotificationView {
  const p = n.payload;
  return {
    id: n.id,
    type: n.type,
    read: n.readAt !== null,
    createdAt: n.createdAt,
    boardId: str(p, 'boardId'),
    boardName: str(p, 'boardName') ?? '',
    commentId: str(p, 'commentId'),
    linkId: str(p, 'linkId'),
    linkName: str(p, 'linkName') ?? '',
    authorName: str(p, 'authorName') ?? '',
    excerpt: str(p, 'excerpt') ?? '',
    guestName: str(p, 'guestName') ?? '',
    message: str(p, 'message') ?? '',
    expiresAt: str(p, 'expiresAt'),
    openItemId: str(p, 'openItemId'),
    title: str(p, 'title') ?? '',
    actorName: str(p, 'actorName') ?? '',
    status: str(p, 'status'),
  };
}

export function unreadCount(list: readonly { read: boolean }[]): number {
  return list.filter((n) => !n.read).length;
}

/** Badge text: "9+" above nine. */
export function badgeText(count: number): string | null {
  if (count <= 0) return null;
  return count > 9 ? '9+' : String(count);
}

export type DayGroup = 'today' | 'yesterday' | 'earlier';

const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

export function dayGroup(createdAt: string, now: Date): DayGroup {
  const t = dayStart(new Date(createdAt));
  const today = dayStart(now);
  if (t >= today) return 'today';
  if (t >= dayStart(new Date(today - 1))) return 'yesterday';
  return 'earlier';
}

export interface NotificationGroup<N> {
  key: DayGroup;
  items: N[];
}

const newestFirst = <N extends { createdAt: string; id: string }>(a: N, b: N) =>
  a.createdAt < b.createdAt
    ? 1
    : a.createdAt > b.createdAt
      ? -1
      : a.id < b.id
        ? 1
        : a.id > b.id
          ? -1
          : 0;

/** Newest first, grouped into today / yesterday / earlier (empty groups omitted). */
export function groupByDay<N extends { createdAt: string; id: string }>(
  list: readonly N[],
  now: Date,
): NotificationGroup<N>[] {
  const groups = new Map<DayGroup, N[]>();
  for (const n of [...list].sort(newestFirst)) {
    const k = dayGroup(n.createdAt, now);
    groups.set(k, [...(groups.get(k) ?? []), n]);
  }
  return (['today', 'yesterday', 'earlier'] as const)
    .filter((k) => groups.has(k))
    .map((key) => ({ key, items: groups.get(key)! }));
}

export type Target =
  | { kind: 'comment'; boardId: string; commentId: string }
  | { kind: 'link'; boardId: string; linkId: string | null }
  | { kind: 'openitem'; boardId: string; openItemId: string }
  | { kind: 'board'; boardId: string };

/** Where clicking the notification leads. */
export function notificationTarget(n: NotificationView): Target | null {
  if (n.boardId === null) return null;
  if (n.type.startsWith('comment.') && n.commentId !== null)
    return { kind: 'comment', boardId: n.boardId, commentId: n.commentId };
  if (n.type.startsWith('openitem.') && n.openItemId !== null)
    return { kind: 'openitem', boardId: n.boardId, openItemId: n.openItemId };
  if (n.type.startsWith('link.')) return { kind: 'link', boardId: n.boardId, linkId: n.linkId };
  return { kind: 'board', boardId: n.boardId };
}

/**
 * Editor URL for a target (`?comment=` opens the thread, `?openitem=` the open item,
 * `?share=` the share dialog).
 */
export function targetPath(t: Target): string {
  const base = `/b/${encodeURIComponent(t.boardId)}`;
  if (t.kind === 'comment') return `${base}?comment=${encodeURIComponent(t.commentId)}`;
  if (t.kind === 'openitem') return `${base}?openitem=${encodeURIComponent(t.openItemId)}`;
  if (t.kind === 'link') return `${base}?share=${encodeURIComponent(t.linkId ?? '1')}`;
  return base;
}

/** Optimistic local "mark read". */
export function markRead<N extends { id: string; read: boolean }>(
  list: readonly N[],
  ids: readonly string[] | 'all',
): N[] {
  return list.map((n) =>
    n.read || (ids !== 'all' && !ids.includes(n.id)) ? n : { ...n, read: true },
  );
}

/** The "Extend link" quick action is offered for extension requests with a known link. */
export function canQuickExtend(n: NotificationView): boolean {
  return n.type === 'link.extension_requested' && n.linkId !== null;
}
